import React from 'react';
import MuiTypography, { TypographyProps } from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

export interface ITypographyProps
  extends TypographyProps<React.ElementType, { component?: string }> {
  bold?: boolean;
  underlined?: boolean;
  italic?: boolean;
  useNewline?: boolean;
  disableLetterSpacing?: boolean;
  breakWord?: boolean;
  testId?: string;
}

const useStyles = makeStyles()({
  newlineContainer: {
    whiteSpace: 'pre-line',
  },
  disableLetterSpacing: {
    letterSpacing: '0',
  },
  breakWord: {
    wordBreak: 'break-word',
  },
} as const);

/**
 * Se https://material-ui.com/components/typography/#typography
 */
export const EDTypography: React.FC<
  React.PropsWithChildren<ITypographyProps & React.RefAttributes<HTMLElement>>
> = React.forwardRef<HTMLElement, ITypographyProps>((props, ref) => {
  const {
    bold,
    underlined,
    italic,
    children,
    className,
    useNewline,
    disableLetterSpacing,
    breakWord,
    testId,
    ...rest
  } = props;
  const { classes, cx } = useStyles();
  let comp: React.ReactNode;

  const finalClassName = cx({
    [className || '']: !!className,
    [classes.newlineContainer]: props.useNewline,
    [classes.disableLetterSpacing]: props.disableLetterSpacing,
    [classes.breakWord]: props.breakWord,
  });

  if (bold && underlined) {
    comp = (
      <strong>
        <u>{children}</u>
      </strong>
    );
  } else if (bold) {
    comp = <strong>{children}</strong>;
  } else if (underlined) {
    comp = <u>{children}</u>;
  } else if (italic) {
    comp = <i>{children}</i>;
  } else {
    comp = children;
  }

  return (
    <MuiTypography
      ref={ref}
      variant={props.variant ? props.variant : 'body2'}
      color={props.color ? props.color : 'textPrimary'}
      className={finalClassName}
      data-testid={testId}
      {...rest}
    >
      {comp}
    </MuiTypography>
  );
});

export default EDTypography;
